import { cx } from "@suse-o11y/class-variance";
import { IconArrowNext } from "@suse-o11y/icons";
import { min } from "@suse-o11y/min";
import { onFrame } from "@suse-o11y/on-frame";
import { onTimeout } from "@suse-o11y/on-timeout";
import React, { useEffect, useRef } from "react";

const MAX_TEXTAREA_HEIGHT_PX = 150;
const RESIZE_DELAY_MS = 0;

export const FollowUpInput: React.FC<{
	disabled: boolean;
	onSubmit: (question: string) => void;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	visible?: boolean;
}> = ({ disabled, onSubmit, value, onChange, placeholder = "Ask a follow-up question...", visible = true }) => {
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		const el = textareaRef.current;
		if (el == null || !visible || value == null) {
			return;
		}

		return onTimeout(() => {
			// Recalculate when shown again after being hidden in the drawer footer.
			el.style.height = "auto";
			el.style.height = `${min(el.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
		}, RESIZE_DELAY_MS);
	}, [value, visible]);

	useEffect(() => {
		if (!visible || disabled) {
			return;
		}
		const el = textareaRef.current;
		if (el == null) {
			return;
		}
		return onFrame(() => {
			el.focus();
		});
	}, [visible, disabled]);

	const handleSubmit = () => {
		if (disabled) {
			return;
		}
		const question = value.trim();
		if (!question) {
			return;
		}
		onSubmit(question);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
			e.preventDefault();
			handleSubmit();
		}
	};

	return (
		<form
			className="flex flex-1 items-end gap-2"
			onSubmit={(e) => {
				e.preventDefault();
				handleSubmit();
			}}
		>
			<div className="flex flex-1 items-end rounded-xl border border-grey-400 bg-grey-100 pr-1 pb-1 shadow-sm transition-colors focus-within:border-primary-500 focus-within:bg-white focus-within:ring-2 focus-within:ring-primary-500/30">
				<textarea
					className="max-h-37.5 min-w-0 flex-1 resize-none overflow-y-auto bg-transparent px-4 py-2.5 text-grey-900 text-sm leading-5.25 outline-none placeholder:text-grey-600"
					onChange={(e) => onChange(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					ref={textareaRef}
					rows={2}
					value={value}
				/>
				<button
					aria-label="Send"
					className={cx(
						"flex size-9 shrink-0 items-center justify-center rounded-lg border transition-colors",
						!value.trim() || disabled
							? "cursor-not-allowed border-grey-300 bg-grey-200 text-grey-500"
							: "cursor-pointer border-primary-600 bg-primary-600 text-white shadow-sm hover:border-primary-500 hover:bg-primary-500",
					)}
					disabled={!value.trim() || disabled}
					type="submit"
				>
					<IconArrowNext className="size-4" />
				</button>
			</div>
		</form>
	);
};
